import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DirectionsBoat as BoatIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getAllCustomers, Customer, CustomerVessel, createCustomerVessel, updateCustomerVessel, deleteCustomerVessel, createCustomer, updateCustomer, deleteCustomer } from '../../../firebase/services/customerVesselService';
import { getAllSlips, Slip, upsertSlip } from '../../../firebase/services/slipService';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { findDuplicateCustomers } from '../../../firebase/services/customerVesselService';

type Order = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'checkbox', label: '', sortable: false },
  { id: 'name', label: 'Name', sortable: true },
  { id: 'companyName', label: 'Company', sortable: true },
  { id: 'contact', label: 'Contact', sortable: true },
  { id: 'vessels', label: 'Vessels', sortable: true },
  { id: 'assignedSlips', label: 'Assigned Slips', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

interface CustomerFormData {
  firstName: string;
  lastName: string;
  companyName?: string;
  email: string;
  phone: string;
  vessels: CustomerVessel[];
  assignedSlips: string[];
}

interface VesselFormData {
  name: string;
  registrationNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  length: number;
  color: string;
  mooringSlip?: string;
}

const CustomersPage = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [slips, setSlips] = useState<Slip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    vessels: [],
    assignedSlips: []
  });
  const [vesselFormData, setVesselFormData] = useState<VesselFormData>({
    name: '',
    registrationNumber: '',
    type: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    length: 0,
    color: ''
  });
  const [openVesselDialog, setOpenVesselDialog] = useState(false);
  const [editingVesselIndex, setEditingVesselIndex] = useState<number | null>(null);
  const [slipSearchTerm, setSlipSearchTerm] = useState('');
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<Customer | null>(null);
  const [mergeTarget, setMergeTarget] = useState<Customer | null>(null);
  const [mergeFields, setMergeFields] = useState<{ [key: string]: any }>({});
  const [merging, setMerging] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('name');
  const navigate = useNavigate();

  // Load customers and slips from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [customersData, slipsData, duplicateGroups] = await Promise.all([
          getAllCustomers(),
          getAllSlips(),
          findDuplicateCustomers()
        ]);
        setCustomers(customersData);
        setSlips(slipsData);
        setDuplicateCount(duplicateGroups.length);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setSelectedCustomer(customer);
      // Find all slips assigned to this customer
      const assignedSlips = slips
        .filter(s => s.customerId === customer.id)
        .map(s => s.slipNumber);
      
      setFormData({
        firstName: customer.firstName,
        lastName: customer.lastName,
        companyName: customer.companyName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        vessels: customer.vessels || [],
        assignedSlips: assignedSlips
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        vessels: [],
        assignedSlips: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVesselFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setVesselFormData(prev => ({
      ...prev,
      [name]: name === 'year' || name === 'length' ? Number(value) : value
    }));
  };

  const handleOpenVesselDialog = (vessel?: CustomerVessel, index?: number) => {
    if (vessel) {
      setVesselFormData({
        name: vessel.name || '',
        registrationNumber: vessel.registrationNumber,
        type: vessel.type,
        make: vessel.make,
        model: vessel.model,
        year: vessel.year,
        length: vessel.length,
        color: vessel.color,
        mooringSlip: vessel.mooringSlip
      });
      setEditingVesselIndex(index ?? null);
    } else {
      setVesselFormData({
        name: '',
        registrationNumber: '',
        type: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        length: 0,
        color: ''
      });
      setEditingVesselIndex(null);
    }
    setOpenVesselDialog(true);
  };

  const handleCloseVesselDialog = () => {
    setOpenVesselDialog(false);
    setEditingVesselIndex(null);
  };

  const handleSaveVessel = async () => {
    if (!selectedCustomer) return;

    try {
      if (editingVesselIndex !== null) {
        // Update existing vessel
        await updateCustomerVessel(
          selectedCustomer.id,
          formData.vessels[editingVesselIndex].vesselId,
          vesselFormData
        );
      } else {
        // Create new vessel
        await createCustomerVessel(selectedCustomer.id, vesselFormData);
      }

      // Refresh customer data
      const updatedCustomers = await getAllCustomers();
      setCustomers(updatedCustomers);
      const updatedCustomer = updatedCustomers.find(c => c.id === selectedCustomer.id);
      if (updatedCustomer) {
        setFormData(prev => ({
          ...prev,
          vessels: updatedCustomer.vessels || []
        }));
      }

      handleCloseVesselDialog();
    } catch (err) {
      console.error('Error saving vessel:', err);
      setError('Failed to save vessel. Please try again.');
    }
  };

  const handleDeleteVessel = async (vesselId: string) => {
    if (!selectedCustomer) return;

    if (window.confirm('Are you sure you want to delete this vessel?')) {
      try {
        await deleteCustomerVessel(selectedCustomer.id, vesselId);
        
        // Refresh customer data
        const updatedCustomers = await getAllCustomers();
        setCustomers(updatedCustomers);
        const updatedCustomer = updatedCustomers.find(c => c.id === selectedCustomer.id);
        if (updatedCustomer) {
          setFormData(prev => ({
            ...prev,
            vessels: updatedCustomer.vessels || []
          }));
        }
      } catch (err) {
        console.error('Error deleting vessel:', err);
        setError('Failed to delete vessel. Please try again.');
      }
    }
  };

  const handleSaveCustomer = async () => {
    try {
      let customerId: string;
      
      if (selectedCustomer) {
        // Update existing customer
        await updateCustomer(selectedCustomer.id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          vessels: formData.vessels
        });
        customerId = selectedCustomer.id;

        // Clear all previously assigned slips
        const previousSlips = slips.filter(s => s.customerId === selectedCustomer.id);
        for (const slip of previousSlips) {
          await upsertSlip({
            ...slip,
            customerId: null,
            renterName: null,
            phoneNumber: null
          });
        }
      } else {
        // Create new customer
        const newCustomer = await createCustomer({
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          vessels: formData.vessels
        });
        customerId = newCustomer.id;
      }

      // Assign all selected slips
      for (const slipNumber of formData.assignedSlips) {
        const slip = slips.find(s => s.slipNumber === slipNumber);
        if (slip) {
          await upsertSlip({
            ...slip,
            customerId: customerId,
            renterName: formData.companyName 
              ? `${formData.companyName} (${formData.firstName} ${formData.lastName})`
              : `${formData.firstName} ${formData.lastName}`,
            phoneNumber: formData.phone
          });
        }
      }

      // Refresh both customers and slips lists
      const [updatedCustomers, updatedSlips] = await Promise.all([
        getAllCustomers(),
        getAllSlips()
      ]);
      setCustomers(updatedCustomers);
      setSlips(updatedSlips);

      handleCloseDialog();
    } catch (err) {
      console.error('Error saving customer:', err);
      setError('Failed to save customer. Please try again.');
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        // TODO: Implement delete functionality
      } catch (err) {
        console.error('Error deleting customer:', err);
        setError('Failed to delete customer. Please try again.');
      }
    }
  };

  const handleRequestSort = (property: string) => {
    if (property === 'checkbox' || property === 'actions') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredCustomers = customers.filter(customer => 
    `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCustomers = React.useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
          break;
        case 'companyName':
          aValue = a.companyName || '';
          bValue = b.companyName || '';
          break;
        case 'contact':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'vessels':
          aValue = (a.vessels || []).length;
          bValue = (b.vessels || []).length;
          break;
        case 'assignedSlips':
          const aSlips = slips.filter(s => s.customerId === a.id).length;
          const bSlips = slips.filter(s => s.customerId === b.id).length;
          aValue = aSlips;
          bValue = bSlips;
          break;
        default:
          return 0;
      }

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined || aValue === '') return 1;
      if (bValue === null || bValue === undefined || bValue === '') return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredCustomers, order, orderBy, slips]);

  // Add function to sort slips
  const getSortedAndFilteredSlips = () => {
    return slips
      .filter(slip => !slip.customerId || slip.customerId === selectedCustomer?.id)
      .filter(slip => 
        slip.slipNumber?.toLowerCase().includes(slipSearchTerm.toLowerCase()) ||
        !slipSearchTerm
      )
      .sort((a, b) => {
        // Extract numbers from slip numbers for proper numerical sorting
        const numA = parseInt(a.slipNumber?.replace(/\D/g, '') || '0');
        const numB = parseInt(b.slipNumber?.replace(/\D/g, '') || '0');
        
        if (numA === numB) {
          // If numbers are equal, sort alphabetically
          return (a.slipNumber || '').localeCompare(b.slipNumber || '');
        }
        return numA - numB;
      });
  };

  // Display loading state
  if (loading && customers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Customers
        </Typography>
        <Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Customer
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const [id1, id2] = selectedForMerge;
              setMergeSource(customers.find(c => c.id === id1) || null);
              setMergeTarget(customers.find(c => c.id === id2) || null);
              setMergeDialogOpen(true);
            }}
            sx={{ ml: 2 }}
            disabled={selectedForMerge.length !== 2}
          >
            Merge Customers
          </Button>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {duplicateCount > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => navigate('/admin/business/customers/duplicates')}
            >
              View Duplicates
            </Button>
          }
        >
          {duplicateCount} potential duplicate {duplicateCount === 1 ? 'customer' : 'customers'} found.
        </Alert>
      )}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search customers by name or email"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {customers.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No customers found.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCustomers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((customer) => {
                  // Find all slips assigned to this customer
                  const assignedSlips = slips
                    .filter(s => s.customerId === customer.id)
                    .map(s => s.slipNumber);
                  
                  return (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedForMerge.includes(customer.id)}
                          onChange={() => {
                            setSelectedForMerge(prev => {
                              if (prev.includes(customer.id)) return prev.filter(id => id !== customer.id);
                              if (prev.length === 2) return prev; // Only allow 2
                              return [...prev, customer.id];
                            });
                          }}
                          disabled={selectedForMerge.length === 2 && !selectedForMerge.includes(customer.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {customer.firstName} {customer.lastName}
                      </TableCell>
                      <TableCell>
                        {customer.companyName || '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {customer.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2">{customer.email}</Typography>
                            </Box>
                          )}
                          {customer.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2">{customer.phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {customer.vessels?.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {customer.vessels.map((vessel) => (
                              <Chip
                                key={vessel.vesselId}
                                icon={<BoatIcon />}
                                label={`${vessel.name || vessel.registrationNumber}`}
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No vessels
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {assignedSlips.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {assignedSlips.map((slipNumber) => (
                              <Chip
                                key={slipNumber}
                                label={`Slip ${slipNumber}`}
                                size="small"
                                color="primary"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No slips assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(customer)}
                            title="Edit Customer"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setCustomerToDelete(customer);
                              setDeleteDialogOpen(true);
                            }}
                            title="Delete Customer"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={sortedCustomers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
      {/* Customer Edit/Create Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Search Slips"
                  value={slipSearchTerm}
                  onChange={e => setSlipSearchTerm(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                  {slips
                    .filter(slip => 
                      (slip.slipNumber || '').toLowerCase().includes(slipSearchTerm.toLowerCase()) &&
                      ((!selectedCustomer && slip.status === 'available') || (selectedCustomer && (slip.status === 'available' || slip.customerId === selectedCustomer.id || slip.customerId === null)))
                    )
                    .sort((a, b) => (a.slipNumber || '').localeCompare(b.slipNumber || ''))
                    .map(slip => (
                      <FormControlLabel
                        key={slip.id}
                        control={
                          <Checkbox
                            checked={formData.assignedSlips.includes(slip.slipNumber)}
                            onChange={e => {
                              const checked = e.target.checked;
                              setFormData(prev => ({
                                ...prev,
                                assignedSlips: checked
                                  ? [...prev.assignedSlips, slip.slipNumber]
                                  : prev.assignedSlips.filter(s => s !== slip.slipNumber)
                              }));
                            }}
                          />
                        }
                        label={slip.slipNumber}
                      />
                    ))}
                  {slips.filter(slip => 
                    (slip.slipNumber || '').toLowerCase().includes(slipSearchTerm.toLowerCase()) &&
                    ((!selectedCustomer && slip.status === 'available') || (selectedCustomer && (slip.status === 'available' || slip.customerId === selectedCustomer.id || slip.customerId === null)))
                  ).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No slips found.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Vessels
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenVesselDialog()}
                >
                  Add Vessel
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {formData.vessels.map((vessel, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {vessel.name || 'Unnamed Vessel'}
                    </Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenVesselDialog(vessel, index)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteVessel(vessel.vesselId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Registration: {vessel.registrationNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {vessel.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Make/Model: {vessel.make} {vessel.model}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Year: {vessel.year}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Length: {vessel.length} ft
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Color: {vessel.color}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Assigned Slip</InputLabel>
                        <Select
                          value={vessel.mooringSlip || ''}
                          label="Assigned Slip"
                          onChange={(e) => {
                            // TODO: Implement slip assignment
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {slips.map(slip => (
                            <MenuItem key={slip.id} value={slip.slipNumber}>
                              Slip {slip.slipNumber}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveCustomer} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Vessel Edit/Create Dialog */}
      <Dialog
        open={openVesselDialog}
        onClose={handleCloseVesselDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingVesselIndex !== null ? 'Edit Vessel' : 'Add New Vessel'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vessel Name"
                name="name"
                value={vesselFormData.name}
                onChange={handleVesselFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Registration Number"
                name="registrationNumber"
                value={vesselFormData.registrationNumber}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Type"
                name="type"
                value={vesselFormData.type}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Make"
                name="make"
                value={vesselFormData.make}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={vesselFormData.model}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={vesselFormData.year}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Length (ft)"
                name="length"
                type="number"
                value={vesselFormData.length}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Color"
                name="color"
                value={vesselFormData.color}
                onChange={handleVesselFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Slip</InputLabel>
                <Select
                  value={vesselFormData.mooringSlip || ''}
                  label="Assigned Slip"
                  onChange={(e) => {
                    setVesselFormData(prev => ({
                      ...prev,
                      mooringSlip: e.target.value
                    }));
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {slips.map(slip => (
                    <MenuItem key={slip.id} value={slip.slipNumber}>
                      Slip {slip.slipNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVesselDialog}>Cancel</Button>
          <Button onClick={handleSaveVessel} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Merge Customers Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => {
        setMergeDialogOpen(false);
        setSelectedForMerge([]);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Merge Customers</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to merge customers. Please try again.
              {/* Optionally show error details for debugging: */}
              {typeof error === 'string' && error !== 'Failed to merge customers. Please try again.' && (
                <Box mt={1} sx={{ wordBreak: 'break-all', fontSize: 12, color: 'gray' }}>{error}</Box>
              )}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source Customer</InputLabel>
                <Select
                  value={mergeSource?.id || ''}
                  onChange={e => setMergeSource(customers.find(c => c.id === e.target.value) || null)}
                  label="Source Customer"
                >
                  {customers.map(c => (
                    <MenuItem key={c.id} value={c.id} disabled={mergeTarget && mergeTarget.id === c.id}>
                      {c.companyName || `${c.firstName} ${c.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Target Customer</InputLabel>
                <Select
                  value={mergeTarget?.id || ''}
                  onChange={e => setMergeTarget(customers.find(c => c.id === e.target.value) || null)}
                  label="Target Customer"
                >
                  {customers.map(c => (
                    <MenuItem key={c.id} value={c.id} disabled={mergeSource && mergeSource.id === c.id}>
                      {c.companyName || `${c.firstName} ${c.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {mergeSource && mergeTarget && (
            <Box mt={3}>
              {['firstName', 'lastName', 'companyName', 'email', 'phone'].map(field => {
                const sourceValue = mergeSource[field] || '';
                const targetValue = mergeTarget[field] || '';
                const selectedRadio = mergeFields[field] || 'target';
                return (
                  <Box key={field} mb={2}>
                    <Typography variant="subtitle2">{field.charAt(0).toUpperCase() + field.slice(1)}</Typography>
                    <RadioGroup
                      row
                      value={selectedRadio}
                      onChange={e => {
                        setMergeFields({
                          ...mergeFields,
                          [field]: e.target.value
                        });
                      }}
                    >
                      <FormControlLabel
                        value="source"
                        control={<Radio />}
                        label={`Source: ${sourceValue || '-'}`}
                      />
                      <FormControlLabel
                        value="target"
                        control={<Radio />}
                        label={`Target: ${targetValue || '-'}`}
                      />
                    </RadioGroup>
                  </Box>
                );
              })}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Vessels and Slips will be combined from both records.</Typography>
            </Box>
          )}
          {merging && <Box mt={2}><CircularProgress size={24} /></Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMergeDialogOpen(false);
            setSelectedForMerge([]);
          }} disabled={merging}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!mergeSource || !mergeTarget || mergeSource.id === mergeTarget.id || merging}
            onClick={async () => {
              if (!mergeSource || !mergeTarget) return;
              setMerging(true);
              try {
                // 1. Prepare merged data
                const mergedData = { ...mergeTarget };
                ['firstName', 'lastName', 'companyName', 'email', 'phone'].forEach(field => {
                  const sourceValue = mergeSource[field] || '';
                  const targetValue = mergeTarget[field] || '';
                  mergedData[field] = (mergeFields[field] === 'source' ? sourceValue : targetValue);
                });
                // 2. Merge vessels (avoid duplicates by registrationNumber)
                const vesselsMap = new Map();
                (mergeTarget.vessels || []).forEach(v => vesselsMap.set(v.registrationNumber, v));
                (mergeSource.vessels || []).forEach(v => vesselsMap.set(v.registrationNumber, v));
                mergedData.vessels = Array.from(vesselsMap.values());
                // 3. Merge assigned slips
                const updatedSlips = slips.map(slip => {
                  if (slip.customerId === mergeSource.id) {
                    return { ...slip, customerId: mergeTarget.id };
                  }
                  return slip;
                });
                // 4. Update target customer
                await updateCustomer(mergeTarget.id, mergedData);
                // 5. Update slips in DB
                for (const slip of updatedSlips) {
                  if (slip.customerId === mergeTarget.id) {
                    await upsertSlip(slip);
                  }
                }
                // 6. Delete source customer
                await deleteCustomer(mergeSource.id);
                // 7. Refresh
                const [customersData, slipsData] = await Promise.all([
                  getAllCustomers(),
                  getAllSlips()
                ]);
                setCustomers(customersData);
                setSlips(slipsData);
                setMergeDialogOpen(false);
                setMergeSource(null);
                setMergeTarget(null);
                setMergeFields({});
                setSelectedForMerge([]);
              } catch (err) {
                console.error('Customer merge error:', err);
                setError(err?.message || String(err) || 'Failed to merge customers. Please try again.');
              } finally {
                setMerging(false);
              }
            }}
          >
            Merge
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Customer Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this customer? This cannot be undone.</Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            {customerToDelete && (customerToDelete.companyName || `${customerToDelete.firstName} ${customerToDelete.lastName}`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!customerToDelete) return;
              try {
                await deleteCustomer(customerToDelete.id);
                const [updatedCustomers, updatedSlips] = await Promise.all([
                  getAllCustomers(),
                  getAllSlips()
                ]);
                setCustomers(updatedCustomers);
                setSlips(updatedSlips);
                setDeleteDialogOpen(false);
                setCustomerToDelete(null);
              } catch (err) {
                setError('Failed to delete customer. Please try again.');
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersPage; 